import type { FC, ReactNode } from 'react'
import type { Tone } from '@/types'

import { useRef } from 'react'
import { cn } from '@/lib/utils'
import { useScrollProgress } from '@/hooks/use-scroll-progress'
import { Cta } from '@/components/generals/cta'
import { SplitWords } from '@/components/generals/split-words'
import { TrialPoints } from '@/components/generals/trial-points'
import { LINKS } from '@/constants/site'

interface CtaLinkData {
    label: string
    href: string
}

interface ClosingCtaProps {
    tone?: Tone
    eyebrow?: string
    lines: [string, string]
    text: ReactNode
    primary?: CtaLinkData
    secondary?: CtaLinkData | null
    points?: readonly string[]
    note?: ReactNode
    id?: string
}

const DARK: Tone[] = ['night', 'deep', 'raised']

/** The closing statement of a page. Its headline widens as it scrolls into place. */
export const ClosingCta: FC<ClosingCtaProps> = ({
    tone = 'night',
    eyebrow = 'Klar for neste jobb?',
    lines,
    text,
    primary = { label: 'Prøv gratis i 14 dager', href: LINKS.signup },
    secondary = { label: 'Bli oppringt', href: '/bli-oppringt' },
    points,
    note,
    id = 'kom-i-gang'
}): ReactNode => {
    const ref = useRef<HTMLElement>(null)
    const firstCount = lines[0].split(' ').length

    useScrollProgress(ref, { startAt: 1, endAt: 0.35 })

    return (
        <section
            ref={ref}
            id={id}
            className={cn(`tone-${tone}`, DARK.includes(tone) && 'grain', 'closing-cta relative overflow-hidden py-28 md:py-44')}
            data-header={DARK.includes(tone) ? 'dark' : 'light'}
        >
            <div className='grid-lines' />

            <div className='wrap relative'>
                <p className='t-label text-tone-muted'>{eyebrow}</p>

                <h2 className='t-display mt-8' data-reveal='words'>
                    <span className='closing-cta-line block md:whitespace-nowrap'>
                        <SplitWords text={lines[0]} />
                    </span>
                    <span className='t-serif block text-tone-ink'>
                        <SplitWords text={lines[1]} start={firstCount} />
                    </span>
                </h2>

                <div className='mt-14 grid grid-cols-12 items-end gap-x-6 gap-y-10'>
                    <div className='col-span-12 md:col-span-6 lg:col-span-5'>
                        <p className='t-lead text-tone-muted pretty'>{text}</p>
                        {note && <p className='mt-4 text-sm text-tone-muted'>{note}</p>}
                    </div>

                    <div className='col-span-12 flex flex-wrap gap-3 md:col-span-6 md:justify-end lg:col-span-7'>
                        <Cta href={primary.href} size='lg' magnetic>
                            {primary.label}
                        </Cta>
                        {secondary && (
                            <Cta href={secondary.href} size='lg' variant='ghost'>
                                {secondary.label}
                            </Cta>
                        )}
                    </div>
                </div>

                <div className='mt-16 border-t border-tone-line pt-6'>
                    <TrialPoints points={points} className='text-tone-muted' />
                </div>
            </div>
        </section>
    )
}
